import { CATALOGUE_POLICES, policesDeLaCharte } from '../../contract/index.js';
/** `@font-face` des polices de la charte, auto-hébergées (D12). */
function fontFaces(charte) {
    return policesDeLaCharte(charte)
        .flatMap((nom) => {
        const definition = CATALOGUE_POLICES[nom];
        return definition.fichiers.map((fichier) => `@font-face {
  font-family: "${definition.famille}";
  src: url("${fichier.chemin}") format("woff2");
  font-weight: ${String(fichier.graisse)};
  font-style: ${fichier.style};
  font-display: swap;
}`);
    })
        .join('\n');
}
function pile(nom) {
    const definition = CATALOGUE_POLICES[nom];
    return `"${definition.famille}", ${definition.repli}`;
}
/**
 * Feuille de style du thème Cimaise, d'après `designs/a-cimaise.html` : cube
 * blanc, vide organisé, aucune icône (2.5). Les deux couleurs de la charte et
 * la disposition de l'accueil entrent par des variables CSS ; le reste est
 * figé, c'est ce qui fait un thème.
 */
export function cssCimaise(charte) {
    return `${fontFaces(charte)}

.achat { margin-block: 24px; }
.bouton-achat { display: inline-flex; align-items: center; min-height: 44px; padding: 12px 20px; border: 1px solid var(--ink); color: var(--ink); text-decoration: none; }
.bouton-achat:focus-visible { outline: 2px solid var(--ink); outline-offset: 4px; }

:root {
  --couleur-principale: ${charte.couleurPrincipale};
  --couleur-secondaire: ${charte.couleurSecondaire};
  --ground: #FFFFFF;
  --ink: var(--couleur-principale);
  --accent: var(--couleur-secondaire);
  --muted: color-mix(in srgb, var(--ink) 55%, #FFFFFF);
  --faint: color-mix(in srgb, var(--ink) 35%, #FFFFFF);
  --hair: color-mix(in srgb, var(--ink) 11%, #FFFFFF);
  --plate: color-mix(in srgb, var(--ink) 10%, #F2F1EC);
  --titres: ${pile(charte.policeTitres)};
  --texte: ${pile(charte.policeTexte)};
  --marge: 40px;
  --blocs: ${String(charte.accueil.length)};
}

@media (prefers-color-scheme: dark) {
  :root:not([data-theme="light"]) {
    --ground: #0B0B0B;
    --ink: color-mix(in srgb, var(--couleur-principale) 18%, #EDEDEA);
    --muted: color-mix(in srgb, var(--ink) 58%, #0B0B0B);
    --faint: color-mix(in srgb, var(--ink) 38%, #0B0B0B);
    --hair: color-mix(in srgb, var(--ink) 14%, #0B0B0B);
    --plate: color-mix(in srgb, var(--ink) 12%, #0B0B0B);
  }
}

*, *::before, *::after { box-sizing: border-box; }

html { background: var(--ground); }

body {
  margin: 0;
  background: var(--ground);
  color: var(--ink);
  font-family: var(--texte);
  font-size: 17px;
  line-height: 1.6;
  -webkit-font-smoothing: antialiased;
}

a { color: inherit; text-decoration: none; }
a:hover { color: var(--accent); }
a:focus-visible, input:focus-visible, textarea:focus-visible, button:focus-visible {
  outline: 1px solid var(--accent);
  outline-offset: 4px;
}

img { max-width: 100%; height: auto; display: block; }

/* <picture> n'est qu'un aiguillage de formats : la mise en page porte sur l'image. */
picture { display: contents; }

h1, h2, h3, h4 { font-family: var(--titres); font-weight: 400; letter-spacing: -0.01em; }

em { font-style: italic; }

.saut {
  position: absolute;
  left: -9999px;
  top: 0;
  background: var(--ground);
  color: var(--ink);
  padding: 12px 18px;
  z-index: 10;
}
.saut:focus { left: 0; }

.invisible {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}

/* Champ piège des formulaires publics : hors de l’écran, jamais rempli à la main. */
.piege {
  position: absolute;
  left: -10000px;
  width: 1px;
  height: 1px;
  overflow: hidden;
}

.wrap { padding: 0 var(--marge); max-width: 1680px; margin: 0 auto; }

.label {
  font-size: 10.5px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--muted);
  margin: 0;
}

.lien-souligne { border-bottom: 1px solid currentColor; }

.sous-label { margin-top: 48px; padding-bottom: 14px; }

.entete {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 32px;
  padding-top: 34px;
  padding-bottom: 90px;
  flex-wrap: wrap;
}
.logo { font-size: 14px; font-weight: 500; letter-spacing: 0.22em; text-transform: uppercase; }
.logo img { display: inline-block; }
.entete nav { display: flex; gap: 26px; font-size: 13px; color: var(--muted); flex-wrap: wrap; }
.entete nav a[aria-current="page"] { color: var(--ink); border-bottom: 1px solid var(--accent); }
.entete .lang { color: var(--faint); letter-spacing: 0.12em; text-transform: uppercase; }

section { padding-bottom: 110px; }
section:last-of-type { padding-bottom: 90px; }

.tete-section {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding-bottom: 34px;
  gap: 24px;
}

.plate { background: var(--plate); width: 100%; }

/* Le fond « plaque » tient la place de l'image tant qu'elle n'est pas arrivée. */
.une .visuel img,
.oeuvre .cadre img,
.fiche-oeuvre .visuels img,
.mosaique img,
.portrait img,
.vignette img { background: var(--plate); }

figure { margin: 0; }
figcaption { font-size: 12.5px; color: var(--muted); margin-top: 8px; }

/* Exposition en cours */
.une .visuel, .bandeau { aspect-ratio: 16 / 9; overflow: hidden; }
.bandeau { margin-bottom: 64px; }
.bandeau img { width: 100%; height: 100%; object-fit: cover; background: var(--plate); }
.une .visuel img { width: 100%; height: 100%; object-fit: cover; }
.une .cartel {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px 40px;
  margin-top: 22px;
  max-width: 900px;
}
.une h1 { font-size: 27px; line-height: 1.2; margin: 10px 0 0; text-wrap: balance; }
.une .dates { font-size: 13.5px; color: var(--muted); margin: 10px 0 0; }
.une p.txt { font-size: 14.5px; color: var(--muted); margin: 0; max-width: 46ch; }
.une .more { display: inline-block; margin-top: 16px; font-size: 13px; }

/* Grille d'œuvres */
.grille { display: grid; grid-template-columns: repeat(3, 1fr); gap: 64px 40px; list-style: none; padding: 0; margin: 0; }
.oeuvre .cadre { display: flex; align-items: flex-end; height: clamp(200px, 22vw, 300px); }
.oeuvre .cadre img { height: 100%; width: auto; max-width: 100%; object-fit: contain; object-position: left bottom; }
.oeuvre dl { margin: 14px 0 0; font-size: 13px; line-height: 1.55; }
.oeuvre dt { font-weight: 500; }
.oeuvre dd { margin: 0; color: var(--muted); }
.oeuvre .prix { margin-top: 7px; color: var(--ink); }
.oeuvre .enq { margin-top: 7px; font-size: 12.5px; color: var(--faint); }
.oeuvre:hover .enq { color: var(--accent); }

/* Listes typographiques */
.colonnes { columns: 3; column-gap: 40px; font-size: 16px; line-height: 2; list-style: none; padding: 0; margin: 0; }
.colonnes li { break-inside: avoid; }
.colonnes a { display: block; }

.rangs { list-style: none; padding: 0; margin: 0; }
.rang {
  display: grid;
  grid-template-columns: 200px 1fr 220px;
  gap: 20px 32px;
  padding: 20px 0;
  border-top: 1px solid var(--hair);
  align-items: baseline;
}
.rangs .rang:last-child { border-bottom: 1px solid var(--hair); }
.rang .d { font-size: 12.5px; color: var(--muted); }
.rang .t { font-size: 17px; }
.rang .t span { display: block; font-size: 13px; color: var(--muted); margin-top: 3px; }
.rang .p { font-size: 12.5px; color: var(--muted); }

/* Pages de contenu */
.page-tete { padding-bottom: 46px; }
.page-tete h1 { font-size: 32px; margin: 10px 0 0; max-width: 24ch; text-wrap: balance; }
.page-tete .chapeau { font-size: 15px; color: var(--muted); margin: 14px 0 0; max-width: 56ch; }

.prose { max-width: 62ch; font-size: 15.5px; }
.prose p { margin: 0 0 1em; }

.deux-colonnes { display: grid; grid-template-columns: 340px 1fr; gap: 56px; align-items: start; }
.portrait img { width: 100%; }

.fiche-oeuvre { display: grid; grid-template-columns: 1.4fr 1fr; gap: 56px; align-items: start; }
.fiche-oeuvre .visuels { display: grid; gap: 24px; }
.fiche-oeuvre .visuels img { width: 100%; }
.fiche-oeuvre .cartel h1 { font-size: 26px; margin: 8px 0 0; }
.fiche-oeuvre .cartel .artiste { font-size: 15px; margin: 0; }
.fiche-oeuvre .cartel dl { margin: 22px 0 0; font-size: 14px; }
.fiche-oeuvre .cartel dt { font-size: 10.5px; letter-spacing: 0.16em; text-transform: uppercase; color: var(--muted); margin-top: 14px; }
.fiche-oeuvre .cartel dd { margin: 3px 0 0; }
.fiche-oeuvre .cartel .prose { margin-top: 26px; }
.fiche-oeuvre .cartel .formulaire { margin-top: 36px; }

.mosaique { display: grid; grid-template-columns: repeat(2, 1fr); gap: 32px; list-style: none; padding: 0; margin: 0; }
.mosaique img { width: 100%; }

/* Formulaires */
.formulaire { max-width: 520px; }
.champ { display: block; margin-bottom: 22px; }
.champ span { display: block; font-size: 10.5px; letter-spacing: 0.16em; text-transform: uppercase; color: var(--muted); margin-bottom: 6px; }
.champ input, .champ textarea {
  width: 100%;
  border: 0;
  border-bottom: 1px solid var(--ink);
  background: transparent;
  color: var(--ink);
  font: inherit;
  font-size: 15px;
  padding: 6px 0;
}
.champ textarea { min-height: 120px; resize: vertical; }
.formulaire button {
  border: 0;
  background: transparent;
  color: var(--ink);
  font: inherit;
  font-size: 13px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  border-bottom: 1px solid var(--ink);
  padding: 0 0 6px;
  cursor: pointer;
}
.formulaire button:hover { color: var(--accent); border-color: var(--accent); }
.formulaire [data-etat] { display: block; margin-top: 14px; font-size: 13px; color: var(--muted); }
.formulaire [data-etat][data-erreur="oui"] { color: var(--accent); }

.newsletter { max-width: 520px; }
.newsletter p { font-size: 14px; color: var(--muted); margin: 10px 0 20px; }
.newsletter .ligne { display: flex; gap: 12px; border-bottom: 1px solid var(--ink); padding-bottom: 8px; }
.newsletter .ligne input { flex: 1; border: 0; background: transparent; color: var(--ink); font: inherit; font-size: 14px; padding: 0; }
.newsletter .ligne button { border: 0; background: transparent; color: var(--ink); font: inherit; font-size: 13px; letter-spacing: 0.1em; text-transform: uppercase; cursor: pointer; padding: 0; }
.newsletter .ligne button:hover { color: var(--accent); }

/* Plan du site */
.plan { display: grid; grid-template-columns: repeat(3, 1fr); gap: 48px 40px; }
.plan ul { list-style: none; padding: 0; margin: 12px 0 0; font-size: 14px; line-height: 1.9; }

/* Pied de page */
.pied {
  border-top: 1px solid var(--hair);
  padding-top: 34px;
  padding-bottom: 60px;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 28px 40px;
  font-size: 13px;
  color: var(--muted);
}
.pied b, .pied h2 {
  display: block;
  color: var(--ink);
  font-family: var(--texte);
  font-weight: 500;
  font-size: 10.5px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  margin: 0 0 10px;
}
.pied ul { list-style: none; padding: 0; margin: 0; }
.pied address { font-style: normal; }
.pied address + address { margin-top: 10px; }
.mention { grid-column: 1 / -1; border-top: 1px solid var(--hair); padding-top: 18px; font-size: 11.5px; color: var(--faint); display: flex; flex-wrap: wrap; gap: 8px 26px; }

@media (max-width: 1000px) {
  .grille { grid-template-columns: repeat(2, 1fr); gap: 48px 28px; }
  .deux-colonnes, .fiche-oeuvre { grid-template-columns: 1fr; gap: 36px; }
  .plan { grid-template-columns: repeat(2, 1fr); }
}

@media (max-width: 760px) {
  :root { --marge: 18px; }
  body { font-size: 16px; }
  .une .cartel { grid-template-columns: 1fr; }
  .rang { grid-template-columns: 1fr; gap: 6px; }
  .pied { grid-template-columns: 1fr 1fr; }
  .colonnes { columns: 2; }
  section { padding-bottom: 72px; }
  .entete { padding-bottom: 56px; }
}

@media (max-width: 520px) {
  .grille, .mosaique, .plan { grid-template-columns: 1fr; }
  .colonnes { columns: 1; }
  .oeuvre .cadre { height: auto; }
  .oeuvre .cadre img { height: auto; width: 100%; }
}

@media print {
  .entete nav, .saut, .newsletter, .formulaire { display: none; }
}
`;
}
//# sourceMappingURL=css.js.map